import { BackLink, Image, Intro, Text } from "components/commons";
import { FormProvider } from "contexts/form-context";
import { FormMode, Path, OrderStatus, ShiftStatus } from "enums";
import locale from "localization";
import { useCallback, useContext, useMemo, useEffect } from "react";
import initialState from "./purchase-order-form.state";
import PurchaseOrderForm from "./purchase-order-form/purchase-order-form";
import { useApi, useModal, useMountAsync, useRouter, useMount } from "hooks";
import ViewPurchaseOrder from "./view-purchase-order";
import { createPurchaseOrderApi, getPurchaseOrderByIdApi, updatePurchaseOrderByIdApi } from "apis";
import { ConfirmModal } from "components/modals";
import { ManDepositSuccess, ManSettled } from "images";
import styles from "./purchase-order-form/purchase-order.module.scss";
import { StationContext, UserContext } from "contexts";
import { combineDateAndTimeInput } from "utils";
import useProducts from "hooks/useProducts";
import { stringToDecimal } from "utils/text.utils";
import { EditRounded } from "@material-ui/icons";
import { getShifts } from "apis/shift-records.api";

const PurchaseOrderDetailsModule = () => {
  const router = useRouter();
  const { user } = useContext(UserContext);
  const { station } = useContext(StationContext);
  const formMode = router.query?.mode || router.query?.pageMode || FormMode.Add;
  const isAdd = formMode === FormMode.Add;
  const isEdit = formMode === FormMode.Edit;
  const isView = formMode === FormMode.View;
  const { setAllProducts } = useProducts({
    withStationCode: true,
  });
  const orderId = router?.query?.id;
  const confirmModal = useModal();
  const successModal = useModal();

  const {
    request: getShiftsRecords,
    result: shiftsRecord,
    loading: isGettingShifts,
  } = useApi({
    api: getShifts,
    params: {
      stationCode: station.stationCode,
    },
  });
  const { request: createPurchaseOrder, loading: isCreating } = useApi({
    api: createPurchaseOrderApi,
  });
  const { request: updatePurchaseOrder, loading: isUpdating } = useApi({
    api: updatePurchaseOrderByIdApi,
    modalError: true,
    cleanObject: false,
  });
  const {
    request: getPurchaseOrderByOrderId,
    result: purchaseOrder,
    loading: isGetting,
  } = useApi({
    api: getPurchaseOrderByIdApi,
  });

  const { order } = purchaseOrder || {};

  useEffect(() => {
    if (isEdit && order?.status) {
      const isAllowedStatus =
        order.status === OrderStatus.ForRevision || order.status === OrderStatus.Processing;

      if (!isAllowedStatus) {
        router.push(Path.ViewPurchaseOrderDetails(orderId, FormMode.View));
      }
    }
  }, [isEdit, order?.status, orderId, router]);

  const handleOnSubmit = useCallback(
    async (fields, error) => {
      const { recipient, date, time, remarks, productType, products } = fields;

      const payload = {
        recipient: recipient.value?.toLowerCase(),
        preferredDeliveryDate: combineDateAndTimeInput(date.value, time.value),
        stationCode: station.stationCode,
        productType: productType?.value,
        remarks: remarks?.value || "",
        orderItems: products.value.map(({ sku, ...rest }) => {
          let value = {};
          if (productType.value === "fuel") {
            value = {
              productCode: sku,
              inventoryLevelCm: stringToDecimal(rest?.inventoryLevelCm),
              inventoryLevelLiter: stringToDecimal(rest?.inventoryLevelLiter),
              volume: stringToDecimal(rest?.volume),
              isInventoryFromTotalizer:
                stringToDecimal(rest?.inventoryLevelLiter) ===
                stringToDecimal(rest?.totalizerValue),
            };
          }

          return {
            ...value,
          };
        }),
        ...(isEdit && order?.status === OrderStatus.ForRevision
          ? { status: OrderStatus.Processing }
          : {}),
      };

      const proceed = async () => {
        const res = isAdd
          ? await createPurchaseOrder(payload)
          : await updatePurchaseOrder(
              {
                id: orderId,
                ...payload,
              },
              () => handleOnSubmit(fields, error),
              { reqError: true }
            );

        if (res?.error) {
          return;
        }
        confirmModal.close();
        successModal.show({
          primary: {
            text: locale.gotIt,
            onClick: () => {
              router.push(Path.PurchaseOrder);
            },
          },
        });
      };

      confirmModal.show({
        primary: {
          text: isAdd ? locale.proceed : locale.saveChanges,
          onClick: () => {
            proceed();
          },
        },
        secondary: {
          text: isAdd ? locale.cancel : locale.continueEditing,
        },
      });
    },
    [
      confirmModal,
      createPurchaseOrder,
      orderId,
      isAdd,
      router,
      station.stationCode,
      successModal,
      updatePurchaseOrder,
      user?.userId,
    ]
  );

  useMountAsync(
    async () => {
      if (!isAdd) {
        await getPurchaseOrderByOrderId({
          id: orderId,
        });
      }
      await getShiftsRecords({
        stationCode: station?.stationCode,
        statuses: [ShiftStatus.Ended, ShiftStatus.Closed].join(","),
        page: 1,
        perPage: 1,
      });
    },
    () => {
      setAllProducts([]);
    }
  );

  const isShowEditOrder = isView ? locale.edit : null;
  const isActionDisabled =
    order?.status === OrderStatus.ForRevision || order?.status === OrderStatus.Processing;

  return (
    <>
      <BackLink text={locale.purchaseOrder} path={Path.PurchaseOrder} />
      <Intro
        title={
          isAdd
            ? locale.placePurchaseOrder
            : `${isEdit ? locale.updatePurchaseOrderID : locale.orderId}: ${
                purchaseOrder?.order?.orderId || "--"
              }`
        }
        {...(isActionDisabled && {
          actionText: isShowEditOrder,
          actionStartIcon: <EditRounded />,
          actionDisabled: !isActionDisabled,
          actionOnClick: () => {
            router.push(Path.ViewPurchaseOrderDetails(orderId, FormMode.Edit));
          },
        })}
      />
      <br />
      <FormProvider
        initialState={useMemo(() => initialState({ purchaseOrder }), [purchaseOrder])}
        onSubmit={handleOnSubmit}
      >
        {isView ? (
          <ViewPurchaseOrder purchaseOrder={purchaseOrder} loading={isGetting} />
        ) : (
          <PurchaseOrderForm
            loading={isCreating || isUpdating || isGetting || isGettingShifts}
            formMode={formMode}
            shiftsRecord={shiftsRecord}
          />
        )}
      </FormProvider>
      <ConfirmModal
        {...confirmModal}
        primary={{
          ...confirmModal?.primary,
          loading: isCreating || isUpdating,
          disabled: isCreating || isUpdating,
        }}
        closable={isEdit}
        title={isEdit ? locale.saveChangesQuestion : ""}
        {...(isAdd ? { centerActions: true } : {})}
      >
        <div className={styles.successModal}>
          {isAdd ? (
            <>
              <Image src={ManDepositSuccess} />
              <br />
              <Text strong className={styles.title}>
                {locale.addPurchaseOrder}
              </Text>
              <Text className={styles.message}>{locale.confirmSavePurchaseOrder}</Text>
            </>
          ) : (
            <Text className={styles.message}>{locale.areYouSureYouWantToSaveTheChangesMade}</Text>
          )}
        </div>
      </ConfirmModal>
      <ConfirmModal {...successModal} closable={false} centerActions>
        <div className={styles.successModal}>
          <Image src={ManSettled} />
          <br />
          <Text strong className={styles.title}>
            {isAdd ? locale.purchaseOrderAdded : locale.updatePurchaseOrder}
          </Text>
          <Text className={styles.message}>
            {isAdd ? locale.purchaseOrderAddedMessage : locale.purchaseOrderUpdatedMessage}
          </Text>
        </div>
      </ConfirmModal>
    </>
  );
};

export default PurchaseOrderDetailsModule;
